import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toCsv, downloadCsv } from "@/lib/csv";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

type Row = {
  id: string;
  reason: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  service: string | null;
  message: string | null;
  ip: string | null;
  user_agent: string | null;
  recaptcha_score: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export default function AdminSpam() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blocked_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setRows((data ?? []) as Row[]);
  };

  useEffect(() => { load(); }, []);

  const reasonOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => set.add(r.reason.split(":")[0]));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (reasonFilter !== "all" && !r.reason.startsWith(reasonFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.email ?? ""} ${r.full_name ?? ""} ${r.message ?? ""} ${r.ip ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, reasonFilter, search]);

  const exportCsv = () => {
    const csv = toCsv(filtered.map(r => ({
      created_at: r.created_at,
      reason: r.reason,
      full_name: r.full_name,
      email: r.email,
      phone: r.phone,
      service: r.service,
      ip: r.ip,
      recaptcha_score: r.recaptcha_score,
      user_agent: r.user_agent,
      message: r.message,
    })));
    downloadCsv(`blocked-submissions-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const deleteRow = async (id: string) => {
    if (!confirm("Delete this blocked submission?")) return;
    const { error } = await supabase.from("blocked_submissions").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    setRows(rs => rs.filter(r => r.id !== id));
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach(r => { const k = r.reason.split(":")[0]; c[k] = (c[k] ?? 0) + 1; });
    return c;
  }, [rows]);

  return (
    <AdminShell
      title="Spam Review"
      actions={
        <>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
          <Button onClick={exportCsv} disabled={!filtered.length}>Export CSV</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["honeypot", "rate_limit", "recaptcha_low_score", "recaptcha_failed", "keyword"].map(k => (
            <div key={k} className="border border-border bg-panel p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{k}</div>
              <div className="text-2xl font-display">{counts[k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Input
            placeholder="Search email, name, message, IP…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Select value={reasonFilter} onValueChange={setReasonFilter}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {reasonOptions.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground font-mono">
            {filtered.length} / {rows.length}
          </div>
        </div>

        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-panel">
              <tr className="text-left font-mono text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3">When</th>
                <th className="p-3">Reason</th>
                <th className="p-3">From</th>
                <th className="p-3">Message</th>
                <th className="p-3">IP</th>
                <th className="p-3">Score</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="p-3 whitespace-nowrap text-muted-foreground font-mono text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary">{r.reason}</span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{r.full_name || "—"}</div>
                    <div className="text-muted-foreground text-xs">{r.email}</div>
                    {r.phone && <div className="text-muted-foreground text-xs">{r.phone}</div>}
                  </td>
                  <td className="p-3 max-w-md">
                    <div className="line-clamp-3 whitespace-pre-wrap">{r.message || "—"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{r.ip}</td>
                  <td className="p-3 font-mono text-xs">
                    {r.recaptcha_score == null ? "—" : r.recaptcha_score.toFixed(2)}
                  </td>
                  <td className="p-3">
                    <button onClick={() => deleteRow(r.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground font-mono text-sm">
                  {loading ? "Loading…" : "No blocked submissions."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}